import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { FileText, Folder as FolderIcon, Image as ImageIcon, Music, Search as SearchIcon } from 'lucide-react';
import { FileSystemNavigator, FileSystemItem } from '../../utils/filesystem';
import { globalNavigator } from '../WindowContent';
import { generateEmbedding384 } from '@/lib/embeddings';
import { isRuVectorAvailable, ruvectorIndexStart, ruvectorIndexStatus, ruvectorSearch } from '@/lib/ruvectorClient';

type SearchProps = {
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  navigator?: FileSystemNavigator;
};

type IndexedItem = {
  name: string;
  path: string;
  type: 'file' | 'folder';
  extension?: string;
};

type SemanticResult = {
  id: string;
  title: string;
  path?: string;
  snippet?: string;
  similarity?: number;
};

const useFileIndex = (nav: FileSystemNavigator) => {
  const [index, setIndex] = useState<IndexedItem[]>([]);

  const buildIndex = async () => {
    // BFS traversal starting at C:\
    const queue: string[] = ['C:\\'];
    const seen = new Set<string>();
    const items: IndexedItem[] = [];

    while (queue.length) {
      const current = queue.shift()!;
      if (seen.has(current)) continue;
      seen.add(current);

      const children: FileSystemItem[] = nav.getItemsInPath(current) || [];
      for (const child of children) {
        items.push({
          name: child.name,
          path: child.path,
          type: child.type,
          extension: child.extension
        });
        if (child.type === 'folder') {
          queue.push(child.path);
        }
      }
    }
    setIndex(items);
  };

  useEffect(() => {
    buildIndex();
    const listener = () => buildIndex();
    window.addEventListener('fileSystemChanged', listener as EventListener);
    return () => window.removeEventListener('fileSystemChanged', listener as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return index;
};

const openItem = async (
  item: IndexedItem,
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void
) => {
  if (!onOpenWindow) return;
  const fileName = item.path.split('\\').pop() || item.name;

  if (item.type === 'folder') {
    onOpenWindow('file-explorer', 'File Explorer', item.path);
    return;
  }

  const ext = (item.extension || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    onOpenWindow('image-viewer', `Image Viewer - ${fileName}`, item.path);
  } else if (ext === 'wav') {
    onOpenWindow('music-player', `Music Player - ${fileName}`, item.path);
  } else if (ext === 'mmoney' || ext === 'mmny') {
    onOpenWindow('maple-money', `MapleMoney - ${fileName}`, item.path);
  } else if (['txt', 'md', 'csv', 'json', 'js', 'ts', 'tsx', 'css', 'ini', 'log', 'conf', 'bat', 'sys', 'mapleslide', 'html'].includes(ext)) {
    onOpenWindow('notepad', `Notepad - ${fileName}`, item.path);
  } else {

    // Fallback to Notepad
    onOpenWindow('notepad', `Notepad - ${fileName}`, item.path);
  }
};

const iconFor = (item: IndexedItem) => {
  if (item.type === 'folder') return <FolderIcon className="h-4 w-4" />;
  const ext = (item.extension || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <ImageIcon className="h-4 w-4" />;
  if (ext === 'wav') return <Music className="h-4 w-4" />;
  return <FileText className="h-4 w-4" />;
};

const Search: React.FC<SearchProps> = ({ onOpenWindow, navigator }) => {
  const nav = navigator || globalNavigator;
  const index = useFileIndex(nav);
  const [query, setQuery] = useState('');
  const [onlyFiles, setOnlyFiles] = useState(false);
  const [onlyFolders, setOnlyFolders] = useState(false);

  // Semantic search state
  const [engine, setEngine] = useState<'local' | 'text'>('text');
  const [semanticResults, setSemanticResults] = useState<SemanticResult[]>([]);
  const [semanticBusy, setSemanticBusy] = useState(false);
  const [indexStatus, setIndexStatus] = useState<any>(null);
  const ruvectorAvailable = useMemo(() => isRuVectorAvailable(), []);

  useEffect(() => {
    if (!ruvectorAvailable) {
      setEngine('text');
      return;
    }
    ruvectorIndexStatus().then(setIndexStatus).catch(() => undefined);
    const t = setInterval(() => {
      ruvectorIndexStatus().then(setIndexStatus).catch(() => undefined);
    }, 1500);
    return () => clearInterval(t);
  }, [ruvectorAvailable]);

  const textResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return index
      .filter((i) => {
        if (onlyFiles && i.type !== 'file') return false;
        if (onlyFolders && i.type !== 'folder') return false;
        return i.name.toLowerCase().includes(q) || i.path.toLowerCase().includes(q);
      })
      .slice(0, 200);
  }, [index, query, onlyFiles, onlyFolders]);

  const doSemanticSearch = async () => {
    const q = query.trim();
    if (!q) return;
    if (!ruvectorAvailable) return;

    setSemanticBusy(true);
    try {
      const emb = await generateEmbedding384(q);
      if (!emb) {
        setSemanticResults([]);
        return;
      }
      const res = await ruvectorSearch({ source: 'os', queryEmbedding: emb, limit: 50 });
      setSemanticResults(res.ok ? res.results : []);
    } finally {
      setSemanticBusy(false);
    }
  };

  const handleBuildIndex = async () => {
    if (!ruvectorAvailable) return;
    setSemanticBusy(true);
    try {
      await ruvectorIndexStart(['documents']);
      const st = await ruvectorIndexStatus();
      setIndexStatus(st);
    } finally {
      setSemanticBusy(false);
    }
  };

  const effectiveResults = engine === 'local' ? semanticResults : textResults;

  return (
    <div className="h-full w-full p-4">
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <SearchIcon className="h-5 w-5" />
            Search
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Input
                placeholder="Search files and folders (e.g., Notes, C:\\User\\Vince\\Desktop)"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (engine === 'local') setSemanticResults([]);
                }}
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && engine === 'local') doSemanticSearch();
                }}
              />
              <Button variant="outline" onClick={() => {
                setQuery('');
                setSemanticResults([]);
              }}>Clear</Button>
              {engine === 'local' && (
                <Button onClick={doSemanticSearch} disabled={semanticBusy || !ruvectorAvailable}>
                  {semanticBusy ? 'Searching…' : 'Semantic Search'}
                </Button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Button
                variant={engine === 'text' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEngine('text')}
              >
                Text
              </Button>
              <Button
                variant={engine === 'local' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEngine('local')}
                disabled={!ruvectorAvailable}
                title={!ruvectorAvailable ? 'Local semantic search is available in the desktop app only.' : undefined}
              >
                Local (Semantic)
              </Button>

              <Separator orientation="vertical" className="mx-1 h-4" />

              <Button
                variant={onlyFiles ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setOnlyFiles((v) => !v);
                  if (!onlyFiles) setOnlyFolders(false);
                }}
              >
                Files
              </Button>
              <Button
                variant={onlyFolders ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setOnlyFolders((v) => !v);
                  if (!onlyFolders) setOnlyFiles(false);
                }}
              >
                Folders
              </Button>

              <Separator orientation="vertical" className="mx-1 h-4" />
              <Badge variant="outline">{index.length} indexed (text)</Badge>

              {engine === 'local' && (
                <>
                  <Badge variant={ruvectorAvailable ? 'outline' : 'secondary'}>
                    {ruvectorAvailable ? 'IntelligenceXP available' : 'Desktop only'}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={handleBuildIndex} disabled={!ruvectorAvailable || semanticBusy}>
                    {semanticBusy ? 'Working…' : 'Build Index'}
                  </Button>
                  {indexStatus?.ok && indexStatus?.status?.running && (
                    <Badge>Indexing…</Badge>
                  )}
                </>
              )}
            </div>
          </div>

          <Separator className="my-1" />

          <ScrollArea className="flex-1">
            <div className="space-y-1">
              {effectiveResults.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  {engine === 'local'
                    ? (ruvectorAvailable ? 'Type a query and press Semantic Search.' : 'Local semantic search is available in the desktop app only.')
                    : 'Start typing to search your virtual drive.'}
                </div>
              ) : (
                engine === 'local'
                  ? (effectiveResults as SemanticResult[]).map((item) => (
                    <Button
                      key={item.id}
                      variant="ghost"
                      className="w-full justify-start gap-3"
                      onClick={() => {
                        if (!item.path) return;
                        const name = item.path.split('\\').pop() || item.title || 'File';
                        const ext = (name.split('.').pop() || '').toLowerCase();
                        if (item.path.endsWith('\\') || ext === '') {
                          onOpenWindow?.('file-explorer', 'File Explorer', item.path);
                        } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
                          onOpenWindow?.('image-viewer', `Image Viewer - ${name}`, item.path);
                        } else if (ext === 'wav') {
                          onOpenWindow?.('music-player', `Music Player - ${name}`, item.path);
                        } else {
                          onOpenWindow?.('notepad', `Notepad - ${name}`, item.path);
                        }
                      }}
                    >
                      <FileText className="h-4 w-4" />
                      <div className="text-left">
                        <div className="text-sm">{item.title || item.path || item.id}</div>
                        {item.path && <div className="text-[11px] text-muted-foreground">{item.path}</div>}
                        {item.snippet && <div className="text-[11px] text-muted-foreground line-clamp-1">{item.snippet}</div>}
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        {typeof item.similarity === 'number' && (
                          <Badge variant="outline">{(item.similarity * 100).toFixed(1)}%</Badge>
                        )}
                        <Badge variant="outline">local</Badge>
                      </div>
                    </Button>
                  ))
                  : (effectiveResults as IndexedItem[]).map((item) => (
                    <Button
                      key={item.path}
                      variant="ghost"
                      className="w-full justify-start gap-3"
                      onClick={() => openItem(item, onOpenWindow)}
                    >
                      {iconFor(item)}
                      <div className="text-left">
                        <div className="text-sm">{item.name}</div>
                        <div className="text-[11px] text-muted-foreground">{item.path}</div>
                      </div>
                      <div className="ml-auto">
                        <Badge variant="outline">{item.type}</Badge>
                      </div>
                    </Button>
                  ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default Search;